import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { configEntrySchema } from '../schema/configCenter.schema';
import type { ConfigEntryFormValues } from '../schema/configCenter.schema';
import type { ConfigEntryResponse } from '../types/configCenter';

const VALUE_TYPES = ['STRING', 'INT', 'LONG', 'BOOLEAN'] as const;

interface ConfigEntryFormDialogProps {
  /** null = create mode, non-null = edit mode */
  editTarget: ConfigEntryResponse | null;
  isSaving: boolean;
  onSave: (values: ConfigEntryFormValues) => void;
  onClose: () => void;
}

/**
 * ConfigEntryFormDialog — 설정 추가/수정 modal
 * role=dialog · aria-modal=true · focus trap · ESC 닫기
 * 5상태 중 dialog 상태
 */
export function ConfigEntryFormDialog({
  editTarget,
  isSaving,
  onSave,
  onClose,
}: ConfigEntryFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = editTarget !== null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfigEntryFormValues>({
    resolver: zodResolver(configEntrySchema),
    defaultValues: {
      configKey: '',
      configValue: '',
      valueType: 'STRING',
      sensitive: false,
      description: '',
    },
  });

  // 수정 모드: 기존 값으로 폼 채우기
  useEffect(() => {
    if (editTarget) {
      reset({
        configKey: editTarget.configKey,
        configValue: editTarget.sensitive ? '' : editTarget.configValue,
        valueType: editTarget.valueType,
        sensitive: editTarget.sensitive,
        description: editTarget.description ?? '',
      });
    } else {
      reset({
        configKey: '',
        configValue: '',
        valueType: 'STRING',
        sensitive: false,
        description: '',
      });
    }
  }, [editTarget, reset]);

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isSaving]);

  const title = isEdit
    ? t('configCenter.form.title.edit')
    : t('configCenter.form.title.create');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    boxSizing: 'border-box',
  };

  const inputErrorStyle: React.CSSProperties = {
    ...inputStyle,
    border: '1px solid var(--color-danger)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    fontWeight: 600,
  };

  const errorMsgStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  return (
    <>
      {/* backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
        }}
        onClick={() => { if (!isSaving) onClose(); }}
        aria-hidden="true"
      />

      {/* dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="config-form-dialog-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--spacing-4)',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            background: 'var(--color-surface-background)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-6)',
            width: 480,
            maxWidth: '100%',
            boxShadow: 'var(--shadow-xl)',
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 헤더 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--spacing-5)',
            }}
          >
            <h2
              id="config-form-dialog-title"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              aria-label="닫기"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 'var(--font-size-lg)',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                color: 'var(--color-text-muted)',
                lineHeight: 1,
                padding: 'var(--spacing-1)',
              }}
            >
              ✕
            </button>
          </div>

          {/* 폼 */}
          <form
            onSubmit={handleSubmit(onSave)}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
          >
            {/* 키 */}
            <label style={labelStyle}>
              {t('configCenter.form.field.key')}
              <input
                {...register('configKey')}
                type="text"
                id="config-key"
                aria-describedby={errors.configKey ? 'config-key-error' : undefined}
                aria-invalid={!!errors.configKey}
                placeholder={t('configCenter.form.field.keyPlaceholder')}
                readOnly={isEdit}
                style={
                  isEdit
                    ? { ...inputStyle, background: 'var(--color-surface-muted)', cursor: 'not-allowed' }
                    : errors.configKey
                    ? inputErrorStyle
                    : inputStyle
                }
              />
              {errors.configKey && (
                <span id="config-key-error" role="alert" style={errorMsgStyle}>
                  {errors.configKey.message}
                </span>
              )}
              {isEdit && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {t('configCenter.form.error.keyReadonly')}
                </span>
              )}
            </label>

            {/* 값 */}
            <label style={labelStyle}>
              {t('configCenter.form.field.value')}
              <input
                {...register('configValue')}
                type="text"
                id="config-value"
                aria-describedby={errors.configValue ? 'config-value-error' : undefined}
                aria-invalid={!!errors.configValue}
                placeholder={t('configCenter.form.field.valuePlaceholder')}
                style={errors.configValue ? inputErrorStyle : inputStyle}
              />
              {errors.configValue && (
                <span id="config-value-error" role="alert" style={errorMsgStyle}>
                  {errors.configValue.message}
                </span>
              )}
            </label>

            {/* 타입 */}
            <fieldset
              style={{
                border: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-2)',
              }}
            >
              <legend
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 600,
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                {t('configCenter.form.field.type')}
              </legend>
              <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
                {VALUE_TYPES.map((vt) => (
                  <label
                    key={vt}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-1)',
                      fontSize: 'var(--font-size-sm)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      {...register('valueType')}
                      type="radio"
                      value={vt}
                    />
                    {t(`configCenter.type.${vt}`)}
                  </label>
                ))}
              </div>
              {errors.valueType && (
                <span role="alert" style={errorMsgStyle}>
                  {errors.valueType.message}
                </span>
              )}
            </fieldset>

            {/* 민감 */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              <input
                {...register('sensitive')}
                type="checkbox"
                id="config-sensitive"
              />
              {t('configCenter.form.field.sensitive')}
            </label>

            {/* 설명 */}
            <label style={labelStyle}>
              {t('configCenter.form.field.description')}
              <input
                {...register('description')}
                type="text"
                id="config-description"
                aria-describedby={errors.description ? 'config-description-error' : undefined}
                aria-invalid={!!errors.description}
                placeholder={t('configCenter.form.field.descriptionPlaceholder')}
                style={errors.description ? inputErrorStyle : inputStyle}
              />
              {errors.description && (
                <span id="config-description-error" role="alert" style={errorMsgStyle}>
                  {errors.description.message}
                </span>
              )}
            </label>

            {/* 동적 갱신 힌트 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--spacing-2)',
                padding: 'var(--spacing-3)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <span aria-hidden="true">ⓘ</span>
              <span>{t('configCenter.form.hint.dynamic')}</span>
            </div>

            {/* 버튼 */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                justifyContent: 'flex-end',
                marginTop: 'var(--spacing-2)',
              }}
            >
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  opacity: isSaving ? 0.6 : 1,
                }}
              >
                {t('configCenter.form.button.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: isSaving
                    ? 'var(--color-surface-muted)'
                    : 'var(--color-brand-primary)',
                  color: isSaving
                    ? 'var(--color-text-muted)'
                    : 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  opacity: isSaving ? 0.6 : 1,
                }}
              >
                {isSaving ? t('configCenter.form.saving') : t('configCenter.form.button.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
